// Download helpers for KPI-style widgets (kpi, sparkle). Everything the API
// sent — value, formula, counts, comparison, trend points — goes into the
// data exports; the image exports capture the rendered widget card.

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { createRoot } from "react-dom/client";
import type { ReactNode } from "react";
import {
  buildAppliedFiltersRows,
  csvCell,
  titleCase,
} from "src/helpers/csvFilters";

// ─── Generic KPI payload shape (all fields optional) ─────────────────────────

export type KpiExportData = {
  label?: string;
  total_count?: number | string;
  case_id?: string | number;
  case_ids?: Array<string | number>;
  // Columns vary per widget (an appeals KPI sends appeal_task_id/appeal_status,
  // a coverage one won't), so only case_id is known up front.
  case_list?: Array<{ case_id?: string | number } & Record<string, unknown>>;
  value?: number | string;
  value_type?: string;
  formula?: string;
  subtitle?: string;
  average?: number;
  avg_percentage?: string;
  trend_label?: string;
  covered_count?: number;
  referral_count?: number;
  comparison?: {
    prior_value?: number | string | null;
    change?: number | string | null;
    change_percent?: number | string | null;
    trend?: string | null;
  } | null;
  spark?: {
    period?: string;
    value_type?: string;
    average?: number;
    points?: Array<Record<string, unknown>>;
  };
} & Record<string, unknown>;

// ─── Filenames ────────────────────────────────────────────────────────────────

export const exportFileName = (label?: string): string => {
  const slug =
    (label ?? "kpi")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "kpi";
  return `${slug}-${new Date().toISOString().slice(0, 10)}`;
};

// ─── Download plumbing ────────────────────────────────────────────────────────

const triggerDownload = (href: string, filename: string) => {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// ─── CSV ──────────────────────────────────────────────────────────────────────

const isScalar = (v: unknown): v is string | number | boolean =>
  ["string", "number", "boolean"].includes(typeof v);

// ─── Section model ────────────────────────────────────────────────────────────
//
// Both downloads are described as sections once, then rendered as either CSV or
// xlsx. Only xlsx can carry column widths (CSV is plain text with no styling
// layer), which is why the cells are kept as values here instead of being
// joined into strings up front.

export type ExportSection = {
  // Bold single-cell heading above the section.
  title?: string;
  // Bold column-header row.
  header?: string[];
  rows: Array<Array<unknown>>;
};

const appliedFiltersSection = (
  appliedFilters?: Record<string, unknown>,
): ExportSection[] => {
  const rows = buildAppliedFiltersRows(appliedFilters);
  if (rows.length === 0) return [];
  // Provenance first, so it is the first thing read when the file is opened.
  return [{ title: "Applied Filters", header: ["Filter", "Value"], rows }];
};

// A Metric/Value summary of every scalar field (including nested comparison.*),
// then a table of the trend points with every key the API provided.
export const buildKpiSections = (
  data: KpiExportData,
  title?: string,
  description?: string | null,
  appliedFilters?: Record<string, unknown>,
): ExportSection[] => {
  const summary: Array<[string, unknown]> = [];

  if (title) summary.push(["Widget", title]);
  if (description) summary.push(["Description", description]);

  // Preferred order first, then any remaining scalar fields the API added.
  const preferred = [
    "label",
    "total_count",
    "value",
    "value_type",
    "formula",
    "subtitle",
    "covered_count",
    "referral_count",
    "average",
    "avg_percentage",
    "trend_label",
  ];
  const seen = new Set<string>([
    "comparison",
    "spark",
    "case_id",
    "case_ids",
    "case_list",
  ]);
  preferred.forEach((key) => {
    seen.add(key);
    if (isScalar(data[key])) summary.push([titleCase(key), data[key]]);
  });
  Object.keys(data).forEach((key) => {
    if (!seen.has(key) && isScalar(data[key])) {
      summary.push([titleCase(key), data[key]]);
    }
  });

  const cmp = data.comparison;
  if (cmp) {
    Object.entries(cmp).forEach(([key, val]) => {
      if (isScalar(val as unknown))
        summary.push([`Prior Comparison — ${titleCase(key)}`, val]);
    });
  }

  const sections: ExportSection[] = [
    ...appliedFiltersSection(appliedFilters),
    { header: ["Metric", "Value"], rows: summary.map(([k, v]) => [k, v]) },
  ];

  const points = data.spark?.points ?? [];
  if (points.length > 0) {
    const cols = Array.from(
      points.reduce((set, p) => {
        Object.keys(p).forEach((k) => set.add(k));
        return set;
      }, new Set<string>()),
    );
    sections.push({
      title: `Trend (${data.spark?.period ?? "period"})${
        data.spark?.average !== undefined
          ? ` — average ${data.spark.average}`
          : ""
      }`,
      header: cols.map(titleCase),
      rows: points.map((p) => cols.map((c) => p[c])),
    });
  }

  return sections;
};

// Sections → CSV text. Sections are separated by a blank line; the BOM keeps
// Excel reading it as UTF-8.
export const sectionsToCsv = (sections: ExportSection[]): string => {
  const lines: string[] = [];
  sections.forEach((section, index) => {
    if (index > 0) lines.push("");
    if (section.title) lines.push(csvCell(section.title));
    if (section.header) lines.push(section.header.map(csvCell).join(","));
    section.rows.forEach((row) => lines.push(row.map(csvCell).join(",")));
  });
  return "\uFEFF" + lines.join("\r\n");
};

export const buildKpiCsv = (
  data: KpiExportData,
  title?: string,
  description?: string | null,
  appliedFilters?: Record<string, unknown>,
): string =>
  sectionsToCsv(buildKpiSections(data, title, description, appliedFilters));

const isId = (val: unknown): val is string | number =>
  typeof val === "string" ? val !== "" : typeof val === "number";

export const getKpiCaseIds = (data: KpiExportData): Array<string | number> => {
  // case_ids is the normal shape, but `case_id` has been seen holding both a
  // single id and an array of them, so both are accepted and flattened rather
  // than one being wrapped into [[...]].
  const candidates: unknown[] = [
    ...(Array.isArray(data.case_ids) ? data.case_ids : []),
    ...(Array.isArray(data.case_id) ? data.case_id : [data.case_id]),
  ];
  return candidates.filter(isId);
};

// Case-list rows are passed through untouched. Projecting them onto a fixed
// set of fields here is what dropped every column the backend added beyond
// case_id/status_name/received_date.
export const getKpiCaseListRows = (
  data: KpiExportData,
): Array<Record<string, unknown>> => {
  if (Array.isArray(data.case_list)) {
    const rows = data.case_list
      // A row is normally an object of columns, but a case_list of bare ids is
      // also accepted — treated as the one column it carries.
      .map((row) =>
        isId(row) ? { case_id: row } : (row as Record<string, unknown>),
      )
      // Any row carrying at least one column counts. Requiring a `case_id`
      // here was a leftover from the fixed three-column export: widgets whose
      // list is keyed on something else (the new-doctors sparkle sends
      // doctor_name/global_doctor_id/npi and no case_id at all) had every row
      // dropped, which disabled the button on a payload full of rows.
      .filter(
        (row): row is Record<string, unknown> =>
          row !== null &&
          typeof row === "object" &&
          Object.keys(row).length > 0,
      );
    if (rows.length > 0) return rows;
  }

  // No usable case_list — fall back to the bare ids so the button still
  // produces a file with the one column it does know about.
  return getKpiCaseIds(data).map((caseId) => ({ case_id: caseId }));
};

// Union of the keys across all rows, in first-seen order — i.e. the order the
// backend wrote them, with any key a later row adds appended.
export const getKpiCaseListColumns = (
  rows: Array<Record<string, unknown>>,
): string[] =>
  Array.from(
    rows.reduce((cols, row) => {
      Object.keys(row).forEach((key) => cols.add(key));
      return cols;
    }, new Set<string>()),
  );

// titleCase() alone yields "Case Id" / "Status Name"; these two keep the
// headers reading as they did when the column set was fixed.
const CASE_LIST_HEADERS: Record<string, string> = {
  case_id: "Case ID",
  status_name: "Case Status",
};

// Acronyms titleCase() would otherwise render as "Id" / "Npi".
const caseListHeader = (key: string): string =>
  CASE_LIST_HEADERS[key] ??
  titleCase(key).replace(/\b(Ids?|Npi)\b/g, (m) => m.toUpperCase());

export const buildCaseListSections = (
  data: KpiExportData,
  appliedFilters?: Record<string, unknown>,
): ExportSection[] => {
  const rows = getKpiCaseListRows(data);
  const cols = getKpiCaseListColumns(rows);
  return [
    ...appliedFiltersSection(appliedFilters),
    {
      header: cols.map(caseListHeader),
      rows: rows.map((row) => cols.map((c) => row[c])),
    },
  ];
};

export const buildKpiCaseListCsv = (
  data: KpiExportData,
  appliedFilters?: Record<string, unknown>,
): string => sectionsToCsv(buildCaseListSections(data, appliedFilters));

// Generic flat-table export — for data that isn't a "case list" (no ID/status
// column conventions to special-case), just plain rows of arbitrary keys.
// Columns are still derived automatically via getKpiCaseListColumns/titleCase,
// same as the case-list path, just without its header overrides.
export const buildTableSections = (
  rows: Array<Record<string, unknown>>,
  appliedFilters?: Record<string, unknown>,
): ExportSection[] => {
  const cols = getKpiCaseListColumns(rows);
  return [
    ...appliedFiltersSection(appliedFilters),
    {
      header: cols.map(titleCase),
      rows: rows.map((row) => cols.map((c) => row[c])),
    },
  ];
};

export const downloadTableXlsx = async (
  rows: Array<Record<string, unknown>>,
  filenameBase: string,
  sheetLabel: string,
  appliedFilters?: Record<string, unknown>,
) =>
  downloadSectionsXlsx(
    buildTableSections(rows, appliedFilters),
    `${exportFileName(filenameBase)}.xlsx`,
    sheetLabel,
  );

// ─── XLSX ─────────────────────────────────────────────────────────────────────
//
// Column widths are the reason these downloads are xlsx and not csv: a .csv
// file has no styling layer at all, so Excel always applies its own default
// width no matter what the file contains.

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// `wch` is measured in characters, so the longest cell's length is the width.
// Only clamped at the bottom (narrow columns still need room for their
// header) and at Excel's own hard ceiling — not at an arbitrary width. A
// description/address column should get as wide as its longest cell, however
// long that is, so the content is never cut off.
const MIN_COL_WIDTH = 10;
const EXCEL_MAX_COL_WIDTH = 95;
const COL_WIDTH_PADDING = 2;

// Row height mirrors the column-width policy above: size to content, only
// clamped at Excel's own technical ceiling — never truncated at an arbitrary
// number. 100pt is where a normal wrapped cell settles (~6 lines); a cell
// that genuinely needs more than that to display without being cut off
// keeps growing, up to Excel's real per-row maximum of 409pt.
const LINE_HEIGHT_PT = 15; // Excel's default single-line height at 11pt Calibri
const EXCEL_MAX_ROW_HEIGHT_PT = 409;

const cellLength = (val: unknown): number =>
  val === null || val === undefined ? 0 : String(val).length;

// How many wrapped lines a cell needs at a given column width: an explicit
// newline always starts a new line, and within each line-break-delimited
// segment the text soft-wraps roughly every `colWidthChars` characters (the
// same character-count unit `wch`/columnWidths already uses).
const estimatedLineCount = (val: unknown, colWidthChars: number): number => {
  if (val === null || val === undefined) return 1;
  const perLine = Math.max(colWidthChars, 1);
  return String(val)
    .split("\n")
    .reduce(
      (sum, line) => sum + Math.max(1, Math.ceil(line.length / perLine)),
      0,
    );
};

// undefined (rather than a number) leaves Excel's own default row height
// alone for the overwhelming majority of rows that only ever need one line —
// only a row with a genuinely multi-line cell gets an explicit height.
const rowHeight = (cells: unknown[], widths: number[]): number | undefined => {
  // Explicit <number> because Array<unknown>.reduce's inference picks the
  // "same type as the array" overload once the seed value happens to be
  // assignable to `unknown` (everything is) — without it, `max` types as
  // `unknown` throughout instead of `number`.
  const maxLines = cells.reduce<number>(
    (max, cell, i) =>
      Math.max(max, estimatedLineCount(cell, widths[i] ?? MIN_COL_WIDTH)),
    1,
  );
  if (maxLines <= 1) return undefined;
  return Math.min(maxLines * LINE_HEIGHT_PT, EXCEL_MAX_ROW_HEIGHT_PT);
};

const columnWidths = (sections: ExportSection[]): number[] => {
  const longest: number[] = [];
  const consider = (cells: unknown[]) =>
    cells.forEach((cell, i) => {
      longest[i] = Math.max(longest[i] ?? 0, cellLength(cell));
    });

  sections.forEach((section) => {
    if (section.title) consider([section.title]);
    if (section.header) consider(section.header);
    section.rows.forEach(consider);
  });

  // Built densely — a section with fewer columns than another would otherwise
  // leave holes in the array, and Array.prototype.map skips holes.
  return Array.from({ length: longest.length }, (_, i) =>
    Math.min(
      Math.max((longest[i] ?? 0) + COL_WIDTH_PADDING, MIN_COL_WIDTH),
      EXCEL_MAX_COL_WIDTH,
    ),
  );
};

// Excel rejects these characters in a sheet name and caps it at 31 chars.
const sheetName = (name: string): string =>
  name
    .replace(/[[\]:*?/\\]/g, " ")
    .trim()
    .slice(0, 31) || "Data";

export const downloadSectionsXlsx = async (
  sections: ExportSection[],
  filename: string,
  tabName: string,
) => {
  // Loaded on demand: exceljs is large, and nothing needs it until the user
  // actually asks for a download.
  const ExcelJS = (await import("exceljs")).default;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName(tabName));

  // Computed once up front so both the column-width pass and the row-height
  // pass below size against the exact same widths.
  const widths = columnWidths(sections);

  sections.forEach((section, index) => {
    if (index > 0) sheet.addRow([]);
    if (section.title) {
      const row = sheet.addRow([section.title]);
      row.font = { bold: true };
      const height = rowHeight([section.title], widths);
      if (height !== undefined) row.height = height;
    }
    if (section.header) {
      const row = sheet.addRow(section.header);
      row.font = { bold: true };
      const height = rowHeight(section.header, widths);
      if (height !== undefined) row.height = height;
    }
    section.rows.forEach((cells) => {
      const row = sheet.addRow(cells);
      const height = rowHeight(cells, widths);
      if (height !== undefined) row.height = height;
    });
  });

  // Set per column rather than assigning sheet.columns — that setter also
  // redefines keys/headers, which would clash with the rows already added.
  widths.forEach((width, i) => {
    sheet.getColumn(i + 1).width = width;
  });

  // Everything is centred — headings, filter labels, text and numbers alike.
  // Excel's defaults would otherwise split the sheet three ways (text left,
  // numbers right, headers left), which reads as ragged once the columns are
  // widened to fit their content. wrapText is what actually makes the taller
  // rows above show their full text instead of a single clipped line, and
  // vertical: "middle" keeps a short single-line cell centred in a row that
  // grew tall because some OTHER cell in that same row needed the room.
  sheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.alignment = {
        ...cell.alignment,
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(new Blob([buffer], { type: XLSX_MIME }), filename);
};

export const downloadKpiCaseList = async (
  data: KpiExportData,
  title?: string,
  appliedFilters?: Record<string, unknown>,
) =>
  downloadSectionsXlsx(
    buildCaseListSections(data, appliedFilters),
    `${exportFileName(data.label ?? title)}-case-list.xlsx`,
    "Case List",
  );

export const downloadKpiCsv = async (
  data: KpiExportData,
  title?: string,
  description?: string | null,
  appliedFilters?: Record<string, unknown>,
) =>
  downloadSectionsXlsx(
    buildKpiSections(data, title, description, appliedFilters),
    `${exportFileName(data.label ?? title)}.xlsx`,
    data.label ?? title ?? "Data",
  );

// ─── PNG / PDF ────────────────────────────────────────────────────────────────
//
// The image exports render a fresh copy of the widget into a hidden, fixed-
// width container and snapshot THAT — never the live dashboard tile. This is
// why a small tile still downloads a clean, full-size image: the export view
// lays out (and re-renders its chart) at EXPORT_WIDTH regardless of how small
// the widget is on screen.

// Fixed width the export view is rendered at (device-independent layout).
export const EXPORT_WIDTH = 760;

const snapshot = (el: HTMLElement): Promise<HTMLCanvasElement> =>
  html2canvas(el, {
    backgroundColor: "#ffffff",
    scale: 2,
    logging: false,
    useCORS: true,
  });

// Mount a React node off-screen at EXPORT_WIDTH, let it paint (and Highcharts
// draw its final, un-animated frame), snapshot it, then tear the mount down.
const captureNode = async (node: ReactNode): Promise<HTMLCanvasElement> => {
  const host = document.createElement("div");
  host.style.cssText =
    `position:fixed;top:0;left:-100000px;width:${EXPORT_WIDTH}px;` +
    `background:#ffffff;z-index:-1;pointer-events:none;`;
  document.body.appendChild(host);

  const root = createRoot(host);
  root.render(node as any);

  // Two rAFs for layout + a short beat so Highcharts finishes its synchronous
  // draw and web-fonts/icons are painted before we rasterize.
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setTimeout(resolve, 160)),
    ),
  );

  const canvas = await html2canvas(host, {
    backgroundColor: "#ffffff",
    scale: 2,
    logging: false,
    useCORS: true,
    width: EXPORT_WIDTH,
    windowWidth: EXPORT_WIDTH,
  });

  root.unmount();
  host.remove();
  return canvas;
};

const canvasToPng = (
  canvas: HTMLCanvasElement,
  data: KpiExportData,
  title?: string,
) =>
  triggerDownload(
    canvas.toDataURL("image/png"),
    `${exportFileName(data.label ?? title)}.png`,
  );

const canvasToPdf = (
  canvas: HTMLCanvasElement,
  data: KpiExportData,
  title?: string,
) => {
  const w = canvas.width / 2;
  const h = canvas.height / 2;
  const pdf = new jsPDF({
    orientation: w >= h ? "landscape" : "portrait",
    unit: "px",
    format: [w + 48, h + 72],
  });

  pdf.addImage(canvas.toDataURL("image/png"), "PNG", 24, 24, w, h);

  // Footer: formula + generated date, so the PDF stands on its own.
  pdf.setFontSize(9);
  pdf.setTextColor(100);
  const footerParts = [
    data.formula ? `Formula: ${data.formula}` : null,
    `Generated ${new Date().toLocaleDateString()}`,
  ].filter(Boolean);
  pdf.text(footerParts.join("  ·  "), 24, h + 48);

  pdf.save(`${exportFileName(data.label ?? title)}.pdf`);
};

// Preferred path: render a dedicated export view (crisp, fixed-size).
export const downloadNodePng = async (
  node: ReactNode,
  data: KpiExportData,
  title?: string,
) => canvasToPng(await captureNode(node), data, title);

export const downloadNodePdf = async (
  node: ReactNode,
  data: KpiExportData,
  title?: string,
) => canvasToPdf(await captureNode(node), data, title);

// Fallback path: snapshot a live element (used only when no export view is
// supplied). Small on-screen tile → small image, hence the node path above.
export const downloadKpiPng = async (
  el: HTMLElement,
  data: KpiExportData,
  title?: string,
) => canvasToPng(await snapshot(el), data, title);

export const downloadKpiPdf = async (
  el: HTMLElement,
  data: KpiExportData,
  title?: string,
) => canvasToPdf(await snapshot(el), data, title);
